import { Check, ClipboardList, Download, Eye, Play, Plus, Power, Search, Trash2, X } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { SectionCard } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Select } from '@/components/ui/select';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { exportarCsv } from '@/lib/export';
import { cn, formatDateValue, formatNumber } from '@/lib/utils';

import { CalibracionNav } from '../components/calibracion-nav';
import { CalibracionStatsPanel } from '../components/calibracion-stats';
import { CodigoHistorialDialog } from '../components/codigo-historial-dialog';
import { GenerarCodigoDialog } from '../components/generar-codigo-dialog';
import {
  useCalibracionStats,
  useCambiarEstadoCodigo,
  useCodigosCalibracion,
  useEliminarCodigo,
} from '../hooks/use-calibracion';
import {
  ESTADOS_CODIGO,
  ESTADO_CODIGO_LABELS,
  estadoCodigoBadge,
  estadoCodigoLabel,
  vigenciaCodigo,
} from '../lib/calibracion-format';
import type { CodigoCalibracion, CodigoFiltros, EstadoCodigo } from '../calibracion.types';

/**
 * Columnas de la tabla de códigos: anchos fijos para los números y flexible
 * para el creador, de modo que las filas se lean en vertical.
 */
const COLUMNAS_CODIGOS =
  'grid grid-cols-[7rem_6.5rem_3.5rem_5.5rem_5.5rem_minmax(0,1fr)_5.75rem_5.75rem_9rem] items-center gap-x-3';

const SELECT_CLASSES =
  'h-10 rounded-control border border-ink-200 bg-white px-2.5 text-sm text-ink-800 transition-colors ' +
  'focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

/**
 * Códigos de calibración (`/monitoreo/calibracion`).
 *
 * Puerto de `Calibration_config` del panel anterior. El listado, los totales y
 * el estado real de cada código los resuelve el backend en una sola consulta.
 */
export function CalibracionCodigosPage() {
  useDocumentTitle('Calibración · Códigos');

  const [filtros, setFiltros] = useState<CodigoFiltros>({});
  const [busqueda, setBusqueda] = useState('');
  const [generando, setGenerando] = useState(false);
  const [enDetalle, setEnDetalle] = useState<number | null>(null);
  const [porEliminar, setPorEliminar] = useState<CodigoCalibracion | null>(null);
  const [aviso, setAviso] = useState<{ tipo: 'success' | 'error'; texto: string } | null>(null);

  const codigos = useCodigosCalibracion(filtros);
  const stats = useCalibracionStats();
  const cambiarEstado = useCambiarEstadoCodigo();
  const eliminar = useEliminarCodigo();

  /** El filtro de texto se aplica en el cliente para no consultar en cada tecla. */
  const visibles = useMemo(() => {
    const termino = busqueda.trim();
    const lista = codigos.data ?? [];

    if (!termino) return lista;

    return lista.filter(
      (codigo) =>
        codigo.codigo.includes(termino) ||
        (codigo.creado_por_nombre ?? '').toLowerCase().includes(termino.toLowerCase()),
    );
  }, [codigos.data, busqueda]);

  const hayFiltros = Boolean(filtros.estado) || busqueda !== '';

  const cambiarEstadoDe = (codigo: CodigoCalibracion, estado: EstadoCodigo) => {
    cambiarEstado.mutate(
      { id: codigo.id, estado },
      {
        onSuccess: () =>
          setAviso({
            tipo: 'success',
            texto: `El código ${codigo.codigo} quedó como ${estadoCodigoLabel(estado).toLowerCase()}.`,
          }),
        onError: (error) =>
          setAviso({ tipo: 'error', texto: error.message || 'No se pudo cambiar el estado.' }),
      },
    );
  };

  const confirmarEliminar = () => {
    if (!porEliminar) return;

    eliminar.mutate(
      { id: porEliminar.id, forzar: porEliminar.estado === 'consumido' },
      {
        onSuccess: () => {
          setAviso({ tipo: 'success', texto: `El código ${porEliminar.codigo} fue eliminado.` });
          setPorEliminar(null);
        },
        onError: (error) =>
          setAviso({ tipo: 'error', texto: error.message || 'No se pudo eliminar el código.' }),
      },
    );
  };

  const exportar = () => {
    exportarCsv(
      'codigos-calibracion',
      ['Código', 'Estado', 'Duración (días)', 'Cantidad de usos', 'Usos restantes', 'Usos registrados', 'Creado por', 'Creación', 'Expiración', 'Descripción'],
      visibles.map((codigo) => [
        codigo.codigo,
        estadoCodigoLabel(codigo.estado_efectivo),
        codigo.duracion_dias,
        codigo.cantidad_usos,
        codigo.usos_restantes,
        codigo.total_usos_registrados,
        codigo.creado_por_nombre ?? '',
        formatDateValue(codigo.fecha_creacion),
        codigo.fecha_expiracion ? formatDateValue(codigo.fecha_expiracion) : '',
        codigo.descripcion ?? '',
      ]),
    );
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">
            Monitoreo y control
          </p>
          <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
            <ClipboardList className="size-6 text-brand-500" aria-hidden="true" />
            Códigos de calibración
          </h1>
          <p className="mt-1 text-sm text-ink-500">
            Códigos de 6 dígitos que autorizan una calibración, con su vigencia y usos.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <CalibracionNav active="codigos" />
          <Button
            size="sm"
            variant="outline"
            leftIcon={<Download className="size-3.5" aria-hidden="true" />}
            onClick={exportar}
            disabled={visibles.length === 0}
          >
            Exportar
          </Button>
          <Button
            size="sm"
            leftIcon={<Plus className="size-3.5" aria-hidden="true" />}
            onClick={() => setGenerando(true)}
          >
            Generar código
          </Button>
        </div>
      </header>

      {aviso ? (
        <Alert
          variant={aviso.tipo === 'success' ? 'success' : 'error'}
          title={aviso.tipo === 'success' ? 'Listo' : 'Revisa el dato'}
        >
          {aviso.texto}
        </Alert>
      ) : null}

      <CalibracionStatsPanel stats={stats.data} loading={stats.isPending} />

      <SectionCard
        title="Códigos"
        description="Estado real considerando vencimiento y usos disponibles"
        action={
          <Badge variant="outline">
            {visibles.length} {visibles.length === 1 ? 'código' : 'códigos'}
          </Badge>
        }
      >
        <div className="flex flex-wrap items-end gap-2">
          <div className="relative min-w-[12rem] flex-1">
            <Search
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-400"
            />
            <input
              type="search"
              value={busqueda}
              onChange={(event) => setBusqueda(event.target.value)}
              placeholder="Buscar por código o creador…"
              aria-label="Buscar código"
              className={cn(
                'h-10 w-full rounded-control border border-ink-200 bg-white pl-9.5 pr-3 text-sm text-ink-900',
                'placeholder:text-ink-400 transition-all focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none',
              )}
            />
          </div>

          <label className="flex flex-col gap-1 text-[11px] font-medium text-ink-500">
            Estado
            <Select
              value={filtros.estado ?? ''}
              onChange={(event) =>
                setFiltros({ ...filtros, estado: event.target.value as EstadoCodigo | '' })
              }
              className={SELECT_CLASSES}
            >
              <option value="">Todos</option>
              {ESTADOS_CODIGO.map((estado) => (
                <option key={estado} value={estado}>
                  {ESTADO_CODIGO_LABELS[estado]}
                </option>
              ))}
            </Select>
          </label>

          {hayFiltros ? (
            <button
              type="button"
              onClick={() => {
                setFiltros({});
                setBusqueda('');
              }}
              className="mb-1.5 inline-flex items-center gap-1.5 rounded-control border border-ink-200 bg-white px-2.5 py-1.5 text-xs font-medium text-ink-600 transition-colors hover:border-brand-300 hover:text-brand-700"
            >
              <X className="size-3.5" aria-hidden="true" />
              Limpiar filtros
            </button>
          ) : null}
        </div>

        <div className="mt-5">
          {codigos.isPending ? (
            <div className="space-y-2">
              {Array.from({ length: 4 }).map((_, index) => (
                <Skeleton key={index} className="h-12 rounded-control" />
              ))}
            </div>
          ) : codigos.isError ? (
            <Alert variant="error" title="No se pudieron cargar los códigos">
              <div className="flex flex-wrap items-center gap-3">
                <span>Verifica la conexión con el servidor e inténtalo nuevamente.</span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => void codigos.refetch()}
                  loading={codigos.isRefetching}
                >
                  Reintentar
                </Button>
              </div>
            </Alert>
          ) : visibles.length === 0 ? (
            <div className="rounded-control border border-dashed border-ink-200 px-4 py-12 text-center">
              <ClipboardList className="mx-auto size-5 text-ink-300" aria-hidden="true" />
              <p className="mt-2 text-sm font-medium text-ink-700">Sin códigos para mostrar</p>
              <p className="mt-0.5 text-xs text-ink-500">
                {(codigos.data ?? []).length === 0
                  ? 'Todavía no se ha generado ningún código de calibración.'
                  : 'Ningún código coincide con los filtros aplicados.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <div className="min-w-[56rem]">
                <div
                  className={cn(
                    COLUMNAS_CODIGOS,
                    'border-b border-ink-100 pb-2 text-[11px] font-semibold uppercase tracking-wider text-ink-400',
                  )}
                >
                  <span>Código</span>
                  <span>Estado</span>
                  <span className="text-right">Días</span>
                  <span className="text-right">Usos</span>
                  <span className="text-right">Registrados</span>
                  <span>Creado por</span>
                  <span>Creación</span>
                  <span>Expira</span>
                  <span className="text-right">Acciones</span>
                </div>

                <ul className="mt-1">
                  {visibles.map((codigo) => {
                    const activo = codigo.estado_efectivo === 'activo';

                    return (
                      <li
                        key={codigo.id}
                        className={cn(
                          COLUMNAS_CODIGOS,
                          'border-b border-ink-100 py-2 last:border-b-0',
                        )}
                      >
                        <span className="flex min-w-0 flex-col">
                          <span className="font-mono text-sm font-bold tracking-wider text-ink-900 tabular-nums">
                            {codigo.codigo}
                          </span>
                          <span
                            className={cn(
                              'truncate text-[11px]',
                              codigo.expirado ? 'text-amber-600' : 'text-ink-400',
                            )}
                          >
                            {vigenciaCodigo(codigo)}
                          </span>
                        </span>

                        <span>
                          <Badge variant={estadoCodigoBadge(codigo.estado_efectivo)}>
                            {estadoCodigoLabel(codigo.estado_efectivo)}
                          </Badge>
                        </span>

                        <span className="text-right font-mono text-xs text-ink-600 tabular-nums">
                          {formatNumber(codigo.duracion_dias)}
                        </span>

                        <span className="text-right font-mono text-xs text-ink-800 tabular-nums">
                          {formatNumber(codigo.usos_restantes)}
                          <span className="text-ink-300"> / </span>
                          {formatNumber(codigo.cantidad_usos)}
                        </span>

                        <span className="text-right font-mono text-xs text-ink-500 tabular-nums">
                          {formatNumber(codigo.total_usos_registrados)}
                        </span>

                        <span className="truncate text-xs text-ink-600">
                          {codigo.creado_por_nombre ?? '—'}
                        </span>

                        <span className="text-xs text-ink-500">
                          {formatDateValue(codigo.fecha_creacion)}
                        </span>

                        <span className="text-xs text-ink-500">
                          {codigo.fecha_expiracion ? formatDateValue(codigo.fecha_expiracion) : '—'}
                        </span>

                        <span className="flex items-center justify-end gap-1">
                          <button
                            type="button"
                            title="Ver historial"
                            aria-label={`Ver historial del código ${codigo.codigo}`}
                            onClick={() => setEnDetalle(codigo.id)}
                            className="flex size-8 items-center justify-center rounded-control text-ink-400 transition-colors hover:bg-ink-100 hover:text-ink-700"
                          >
                            <Eye className="size-4" aria-hidden="true" />
                          </button>

                          {activo ? (
                            <button
                              type="button"
                              title="Desactivar"
                              aria-label={`Desactivar el código ${codigo.codigo}`}
                              disabled={cambiarEstado.isPending}
                              onClick={() => cambiarEstadoDe(codigo, 'inactivo')}
                              className="flex size-8 items-center justify-center rounded-control text-ink-400 transition-colors hover:bg-amber-50 hover:text-amber-600"
                            >
                              <Power className="size-4" aria-hidden="true" />
                            </button>
                          ) : (
                            <button
                              type="button"
                              title="Activar"
                              aria-label={`Activar el código ${codigo.codigo}`}
                              disabled={cambiarEstado.isPending}
                              onClick={() => cambiarEstadoDe(codigo, 'activo')}
                              className="flex size-8 items-center justify-center rounded-control text-ink-400 transition-colors hover:bg-emerald-50 hover:text-emerald-600"
                            >
                              {codigo.estado_efectivo === 'consumido' ? (
                                <Check className="size-4" aria-hidden="true" />
                              ) : (
                                <Play className="size-4" aria-hidden="true" />
                              )}
                            </button>
                          )}

                          <button
                            type="button"
                            title="Eliminar"
                            aria-label={`Eliminar el código ${codigo.codigo}`}
                            onClick={() => setPorEliminar(codigo)}
                            className="flex size-8 items-center justify-center rounded-control text-ink-400 transition-colors hover:bg-rose-50 hover:text-rose-600"
                          >
                            <Trash2 className="size-4" aria-hidden="true" />
                          </button>
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          )}
        </div>
      </SectionCard>

      <GenerarCodigoDialog open={generando} onClose={() => setGenerando(false)} />

      <CodigoHistorialDialog codigoId={enDetalle} onClose={() => setEnDetalle(null)} />

      <ConfirmDialog
        open={porEliminar !== null}
        title="Eliminar código"
        description={
          porEliminar
            ? `Se eliminará el código ${porEliminar.codigo} y su historial de uso.`
            : ''
        }
        confirmLabel="Eliminar"
        loading={eliminar.isPending}
        onConfirm={confirmarEliminar}
        onClose={() => setPorEliminar(null)}
      >
        <div className="space-y-3">
          <p className="text-sm leading-relaxed text-ink-600">
            {porEliminar?.total_usos_registrados
              ? `Este código tiene ${porEliminar.total_usos_registrados} uso(s) registrado(s): se perderá el historial asociado.`
              : 'El código no registra usos, así que no se pierde trazabilidad.'}
          </p>
          {porEliminar?.estado === 'consumido' ? (
            <p className="text-sm leading-relaxed text-amber-700">
              El código ya fue consumido: si lo eliminas desaparece del historial de
              calibraciones autorizadas.
            </p>
          ) : null}
        </div>
      </ConfirmDialog>
    </div>
  );
}
